import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, Mail, Building2, Calendar } from "lucide-react";

function toCsv(rows) {
  if (!rows.length) return "";
  const headers = ["created_at", "name", "email", "company", "property_count", "notes"];
  const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const body = rows.map((r) => headers.map((h) => esc(r[h])).join(","));
  return [headers.join(","), ...body].join("\n");
}

export default function AdminWaitlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/waitlist").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const download = () => {
    const csv = toCsv(items);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `guest-smell-waitlist-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700" data-testid="waitlist-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to console
        </Link>
        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Admin · Pilot Leads</div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]">Verify™ Waitlist</h1>
            <p className="mt-2 text-slate-500 text-sm">{items.length} signup{items.length === 1 ? "" : "s"} · newest first</p>
          </div>
          <Button onClick={download} disabled={!items.length} data-testid="waitlist-download-csv"
            className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 rounded-2xl border border-dashed border-slate-300 text-center text-slate-500" data-testid="waitlist-empty">
              No signups yet.
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold">
                <div className="col-span-3">Signed up</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Company</div>
                <div className="col-span-2">Portfolio</div>
                <div className="col-span-1 text-right">Contact</div>
              </div>
              {items.map((w) => (
                <div key={w.id} data-testid={`waitlist-row-${w.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b last:border-b-0 items-center">
                  <div className="md:col-span-3 flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    {new Date(w.created_at).toLocaleString()}
                  </div>
                  <div className="md:col-span-3">
                    <div className="font-medium text-[#0A1128]">{w.name}</div>
                    {w.notes && <div className="text-xs text-slate-500 line-clamp-2 mt-1">{w.notes}</div>}
                  </div>
                  <div className="md:col-span-3 flex items-center gap-2 text-sm text-slate-700">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    {w.company || <span className="text-slate-400">—</span>}
                  </div>
                  <div className="md:col-span-2 text-sm">
                    {w.property_count ? (
                      <span className="px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">{w.property_count}</span>
                    ) : <span className="text-slate-400 text-sm">—</span>}
                  </div>
                  <div className="md:col-span-1 md:text-right">
                    <a href={`mailto:${w.email}`} className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-sm" data-testid={`mailto-${w.id}`}>
                      <Mail className="w-3.5 h-3.5" /> Email
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
